import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Observable, filter, map, of, switchMap, take } from 'rxjs';
import { AuthService } from './auth.service';
import { MerchantService } from './merchant.service';
import { AWAITING_APPROVAL_SECTIONS } from '../services/merchant-context.service';

const signedIn = (msal: MsalService): boolean =>
  msal.instance.getActiveAccount() !== null || msal.instance.getAllAccounts().length > 0;

/**
 * Emits once MSAL has finished whatever it was doing, the response to a sign-in redirect above all. Until then
 * there is no account yet, and deciding early would send a user who has just signed in — or whose sign-in has
 * just failed — straight back to the user flow.
 */
const msalSettled = (): Observable<unknown> =>
  inject(MsalBroadcastService).inProgress$.pipe(
    filter((status) => status === InteractionStatus.None),
    take(1)
  );

/**
 * Requires a Microsoft Entra External ID account only. Used by screens that exist before there is a merchant to
 * act on — /onboarding most of all, which authGuard would otherwise bounce straight back.
 *
 * There is no sign-in screen of our own to send people to: External ID owns the credentials, so an
 * unauthenticated visitor goes straight to the External ID user flow. A cancelled or failed sign-in lands on
 * /login-failed (AuthService), which is unguarded, so this cannot loop.
 */
export const signedInGuard: CanActivateFn = (_route, state) => {
  const msal = inject(MsalService);
  const auth = inject(AuthService);

  return msalSettled().pipe(
    map(() => {
      if (signedIn(msal)) return true;

      auth.login(state.url);
      return false;
    })
  );
};

/**
 * Requires a signed-in account AND a merchant account behind it.
 *
 * The merchant is fetched from /auth/me, so this resolves asynchronously the first time. An
 * identity that signed up but never registered is sent to the /register wizard rather than into
 * an app that has no account to show it. An account whose application isn't approved yet is sent
 * to the application's overview: until then the product has nothing it may do (Klidi.Api refuses
 * money movement for it as well).
 */
export const authGuard: CanActivateFn = (_route, state): Observable<boolean | UrlTree> => {
  const msal = inject(MsalService);
  const auth = inject(AuthService);
  const merchants = inject(MerchantService);
  const router = inject(Router);

  return msalSettled().pipe(
    switchMap(() => {
      if (!signedIn(msal)) {
        auth.login(state.url);
        return of(false);
      }

      return merchants.ensureLoaded().pipe(
        map((merchant) => {
          if (merchant?.awaitingApproval) {
            const section = state.url.split('?')[0].split('/')[1] ?? '';
            return AWAITING_APPROVAL_SECTIONS.has(section)
              ? true
              : router.createUrlTree(['/application/individual-dashboard']);
          }
          if (merchant) return true;
          // No merchant behind a valid identity: register, or let the interceptor's 401 handling take
          // over if the call failed for some other reason.
          return merchants.requiresOnboarding() ? router.createUrlTree(['/register']) : true;
        })
      );
    })
  );
};
